import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Loader2, UserPlus, Eye, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

export default function CoachesAdminPanel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [hours, setHours] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', levels: [] });
  const [report, setReport] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, h] = await Promise.all([
        axios.get(`${API}/admin/coaches`, { withCredentials: true }),
        axios.get(`${API}/admin/coach-hours`, { withCredentials: true }),
      ]);
      setData(c.data); setHours(h.data.entries);
    } catch (e) { toast.error('Chargement impossible.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!form.first_name || !form.email) return toast.error('Prénom et email requis.');
    try {
      await axios.post(`${API}/admin/coaches/invite`, form, { withCredentials: true });
      toast.success(`Invitation envoyée à ${form.email} (lien sécurisé 48h).`);
      setInviteOpen(false); setForm({ first_name: '', last_name: '', email: '', phone: '', levels: [] });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur.'); }
  };

  const act = async (url, msg) => {
    try { await axios.post(`${API}${url}`, {}, { withCredentials: true }); toast.success(msg); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur.'); }
  };

  const review = async (id, action) => {
    let corrected_min = null, note = null;
    if (action === 'correct') { corrected_min = parseInt(window.prompt('Durée corrigée (minutes) :') || '0', 10); if (!corrected_min) return; }
    if (action === 'contest') note = window.prompt('Motif de contestation :') || '';
    try {
      await axios.post(`${API}/admin/coach-hours/${id}/review`, { action, corrected_min, note }, { withCredentials: true });
      toast.success('Heures traitées.');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur.'); }
  };

  const openReport = async (coach) => {
    const month = new Date().toISOString().slice(0, 7);
    try {
      const r = await axios.get(`${API}/admin/coaches/${coach.id}/monthly-report?month=${month}`, { withCredentials: true });
      setReport(r.data);
    } catch (e) { toast.error('Erreur relevé.'); }
  };

  if (!data) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#C99A55]" /></div>;

  const pending = hours.filter((h) => h.status === 'declared');

  return (
    <div className="space-y-5" data-testid="coaches-admin-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg text-[#493329]">Coachs PURO ({data.coaches.length})</h3>
        <Button onClick={() => setInviteOpen(true)} className="bg-[#9A744C] hover:bg-[#493329] text-white rounded-full h-9 text-xs" data-testid="invite-coach-btn">
          <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Nouvelle coach
        </Button>
      </div>

      {data.coaches.length === 0 && <p className="text-sm text-[#AE9885]">Aucun coach. Envoyez votre première invitation.</p>}
      {data.coaches.map((c) => (
        <div key={c.id} className="bg-white border border-[#E9DDCC] rounded-2xl p-4" data-testid={`coach-row-${c.email}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="font-heading text-[#493329]">{c.name} <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full ${c.coach_status === 'actif' ? 'bg-[#EDF2EA] text-[#5C7057]' : c.coach_status === 'a_valider' ? 'bg-[#FBF3DC] text-[#9A744C]' : 'bg-[#F5EFE5] text-[#7A6455]'}`}>{c.status_label}</span></p>
              <p className="text-xs text-[#7A6455]">{c.email}{c.phone ? ` · ${c.phone}` : ''} · {(c.levels || []).join(', ') || 'Niveaux non définis'}</p>
              <p className="text-xs text-[#7A6455] mt-0.5">
                {c.slots.length} créneau(x) · Prochain : {c.next_course || '—'} · Remplissage {c.fill_rate}% ·
                Heures : {c.hours.planned}h prévues / {c.hours.declared}h déclarées / {c.hours.validated}h validées
              </p>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {c.coach_status === 'a_valider' && (
                <Button size="sm" onClick={() => act(`/admin/coaches/${c.id}/validate`, 'Coach validé — compte actif.')} className="h-8 text-xs bg-[#5C7057] hover:bg-[#493329] text-white rounded-full" data-testid={`validate-${c.email}`}>Valider le coach</Button>
              )}
              {c.coach_status === 'invited' && (
                <Button size="sm" variant="outline" onClick={() => act(`/admin/coaches/${c.id}/resend-invite`, 'Invitation renvoyée.')} className="h-8 text-xs rounded-full border-[#E9DDCC]" data-testid={`resend-${c.email}`}>Renvoyer l'invitation</Button>
              )}
              <Button size="sm" variant="outline" onClick={() => navigate(`/espace-coach?preview=${c.id}`)} className="h-8 text-xs rounded-full border-[#E9DDCC] text-[#9A744C]" data-testid={`impersonate-${c.email}`}>
                <Eye className="w-3 h-3 mr-1" /> Voir son espace Coach
              </Button>
              <Button size="sm" variant="outline" onClick={() => openReport(c)} className="h-8 text-xs rounded-full border-[#E9DDCC]" data-testid={`report-${c.email}`}>
                <Printer className="w-3 h-3 mr-1" /> Relevé mensuel
              </Button>
              <Button size="sm" variant="outline" onClick={() => act(`/admin/coaches/${c.id}/toggle-active`, c.active ? 'Coach désactivé.' : 'Coach réactivé.')} className={`h-8 text-xs rounded-full border-[#E9DDCC] ${c.active ? 'text-[#C76542]' : 'text-[#5C7057]'}`} data-testid={`toggle-${c.email}`}>
                {c.active ? 'Désactiver' : 'Réactiver'}
              </Button>
            </div>
          </div>
        </div>
      ))}

      {/* Heures à valider */}
      <div className="bg-white border border-[#E9DDCC] rounded-2xl p-4" data-testid="hours-validation-block">
        <p className="text-[10px] uppercase tracking-wider text-[#7A6455] font-semibold mb-2">Heures en attente de validation ({pending.length})</p>
        {pending.length === 0 && <p className="text-xs text-[#AE9885]">Aucune heure en attente.</p>}
        {pending.map((h) => (
          <div key={h.id} className="flex flex-wrap items-center justify-between gap-2 py-2 border-t border-[#EFE4D0] text-sm">
            <span className="text-[#493329]">{h.coach_name} · {new Date(h.date + 'T00:00').toLocaleDateString('fr-FR')} {h.start}–{h.end} · {h.participants} part. · {h.duration_min} min</span>
            <span className="flex gap-1.5">
              <Button size="sm" onClick={() => review(h.id, 'validate')} className="h-7 text-xs bg-[#5C7057] text-white rounded-full" data-testid={`hval-${h.id}`}>Valider</Button>
              <Button size="sm" variant="outline" onClick={() => review(h.id, 'correct')} className="h-7 text-xs rounded-full border-[#E9DDCC]">Corriger</Button>
              <Button size="sm" variant="outline" onClick={() => review(h.id, 'contest')} className="h-7 text-xs rounded-full border-[#E9DDCC] text-[#C76542]">Contester</Button>
            </span>
          </div>
        ))}
      </div>

      {/* Dialog invitation */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-normal text-[#493329]">Inviter une nouvelle coach</DialogTitle>
            <DialogDescription className="text-xs text-[#7A6455]">Un lien d'activation sécurisé (48h) lui sera envoyé par email. Aucun mot de passe n'est transmis.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Prénom" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="rounded-xl border-[#E9DDCC]" data-testid="invite-first-name" />
              <Input placeholder="Nom" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="rounded-xl border-[#E9DDCC]" data-testid="invite-last-name" />
            </div>
            <Input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl border-[#E9DDCC]" data-testid="invite-email" />
            <Input placeholder="Téléphone (facultatif)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl border-[#E9DDCC]" data-testid="invite-phone" />
            <div className="flex gap-2">
              {['Débutant', 'Intermédiaire', 'Dynamique'].map((l) => (
                <button key={l} onClick={() => setForm({ ...form, levels: form.levels.includes(l) ? form.levels.filter((x) => x !== l) : [...form.levels, l] })}
                  className={`text-xs px-3 py-1.5 rounded-full border ${form.levels.includes(l) ? 'bg-[#C99A55] text-white border-transparent' : 'border-[#E9DDCC] text-[#7A6455]'}`}>{l}</button>
              ))}
            </div>
            <Button onClick={invite} className="w-full bg-[#9A744C] hover:bg-[#493329] text-white rounded-xl h-10" data-testid="invite-send">Envoyer l'invitation</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Relevé mensuel */}
      <Dialog open={!!report} onOpenChange={(o) => !o && setReport(null)}>
        <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="monthly-report-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-normal text-[#493329]">PURO — Relevé d'activité Coach</DialogTitle>
            <DialogDescription className="sr-only">Relevé mensuel imprimable</DialogDescription>
          </DialogHeader>
          {report && (
            <div id="releve-print" className="text-sm space-y-3">
              <p><strong>Coach :</strong> {report.coach.name}<br /><strong>Mois :</strong> {report.month}<br />
                <strong>Séances réalisées :</strong> {report.sessions_count}<br />
                <strong>Heures déclarées :</strong> {report.summary.declared} h · <strong>Heures validées PURO :</strong> {report.summary.validated} h · <strong>Heures prévues :</strong> {report.summary.planned} h</p>
              <table className="w-full text-xs border-collapse">
                <thead><tr className="text-left text-[#7A6455] border-b border-[#E9DDCC]"><th className="py-1">Date</th><th>Heure</th><th>Cours</th><th>Durée</th><th>Statut</th></tr></thead>
                <tbody>
                  {report.entries.map((e) => (
                    <tr key={e.id} className="border-b border-[#EFE4D0]">
                      <td className="py-1">{new Date(e.date + 'T00:00').toLocaleDateString('fr-FR')}</td><td>{e.start}</td>
                      <td>{e.type.replace('Pilates Reformer – ', '')}</td><td>{e.duration_min} min</td>
                      <td>{e.status === 'validated' ? 'Validée' : e.status === 'contested' ? 'Contestée' : 'Déclarée'}</td>
                    </tr>
                  ))}
                  {report.entries.length === 0 && <tr><td colSpan={5} className="py-2 text-[#AE9885]">Aucune séance pointée ce mois-ci.</td></tr>}
                </tbody>
              </table>
              <Button onClick={() => window.print()} variant="outline" className="rounded-xl border-[#E9DDCC] text-xs" data-testid="report-print">
                <Printer className="w-3.5 h-3.5 mr-1.5" /> Imprimer / Exporter PDF
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
